import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Users,
  AlertCircle,
  ExternalLink,
  MapPin,
  CalendarDays,
  X,
  Tag,
  Megaphone,
} from 'lucide-react';
import AppLayout from '@components/AppLayout';
import client from '../../src/api/client';
import '../styles/news.css';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';

const CATEGORY_META = {
  Assembly: { tone: 'blue', label: 'General Assembly' },
  Training: { tone: 'purple', label: 'Seminar / Training' },
  Outreach: { tone: 'orange', label: 'Community Outreach' },
  Benefit: { tone: 'green', label: 'Health & Wellness' },
  Other: { tone: 'slate', label: 'Other' },
  'Union Update': { tone: 'orange', label: 'Union Update' },
  Events: { tone: 'blue', label: 'Events' },
  Learning: { tone: 'purple', label: 'Learning' },
  Benefits: { tone: 'green', label: 'Benefits' },
};

const getCategoryMeta = (category) => CATEGORY_META[category] ?? { tone: 'slate', label: category || 'Event' };

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const resolveMediaUrl = (value) => {
  return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
};

const toPositiveInteger = (value) => {
  const parsed = Number.parseInt(String(value ?? '').trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
};

const formatSeatSummary = (item) => {
  const attendees = Number(item?.attendeeCount ?? 0);
  const capacity = Number(item?.capacity);
  if (Number.isFinite(capacity) && capacity > 0) {
    return `${attendees}/${capacity} seats`;
  }
  return `${attendees} attending`;
};

const isAttendingItem = (item) => {
  if (item?.isUserAttending) {
    return true;
  }
  const status = String(item?.userAttendanceStatus ?? '').trim().toLowerCase();
  return status === 'attending' || status === 'registered' || status === 'confirmed';
};

const getPrimaryEventDate = (item) => item?.startAt ?? item?.publishedAt ?? null;

const getPreferredCalendarEvent = (eventsForDay) => {
  if (!Array.isArray(eventsForDay) || eventsForDay.length === 0) {
    return null;
  }
  return eventsForDay.find((item) => isAttendingItem(item)) ?? eventsForDay[0];
};

const normalizeNewsItem = (item) => {
  if (!item || typeof item !== 'object') return null;

  const startAt = item.startAt ?? item.start_at ?? item.publishedAt ?? item.published_at ?? null;
  const endAt = item.endAt ?? item.end_at ?? null;
  const heroImage = resolveMediaUrl(item.heroImage ?? item.previewImage ?? item.imagePath ?? item.image);

  return {
    ...item,
    startAt,
    endAt,
    publishedAt: item.publishedAt ?? item.published_at ?? startAt,
    heroImage,
  };
};

const CALENDAR_WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NEWS_CACHE_KEY_PREFIX = 'alu_news_cache_v1';
const NEWS_CACHE_TTL_MS = 60 * 1000;

/* ── Regular event card (horizontal) ─────────────────── */

function EventCard({ item, onSelect, variant = 'default' }) {
  const meta = getCategoryMeta(item.category);
  const isMyEventsVariant = variant === 'my-events';

  const handleActivate = () => {
    onSelect(item);
  };

  return (
    <article
      className={`ev-card${isMyEventsVariant ? ' ev-card--my-events' : ''}`}
      onClick={isMyEventsVariant ? handleActivate : undefined}
      onKeyDown={
        isMyEventsVariant
          ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleActivate();
            }
          }
          : undefined
      }
      role={isMyEventsVariant ? 'button' : undefined}
      tabIndex={isMyEventsVariant ? 0 : undefined}
    >
      <div className="ev-card__img-wrap">
        <span className={`ev-card__img-badge ev-card__img-badge--${meta.tone}`}>{item.category || 'Event'}</span>
        {item.heroImage ? (
          <img src={item.heroImage} alt={item.title} className="ev-card__img" />
        ) : (
          <div className="ev-card__img-ph"><CalendarDays size={28} /></div>
        )}
      </div>

      <div className="ev-card__body">
        <h3 className="ev-card__title">{item.title}</h3>

        <div className="ev-card__meta ev-card__meta--details">
          {item.startAt && (
            <span className="ev-card__meta-item"><Calendar size={13} />{formatDate(item.startAt)}</span>
          )}
          {item.startAt && (
            <span className="ev-card__meta-item"><Clock size={13} />{formatTime(item.startAt)}</span>
          )}
          {item.venue && (
            <span className="ev-card__meta-item"><MapPin size={13} />{item.venue}</span>
          )}
        </div>

        {!isMyEventsVariant ? (
          <p className="ev-card__desc">
            {item.summary || (item.content ? item.content.substring(0, 100) + (item.content.length > 100 ? '...' : '') : '')}
          </p>
        ) : null}

        {!isMyEventsVariant ? (
          <div className="ev-card__footer">
            <button type="button" className="ev-card__cta-btn" onClick={handleActivate}>
              View Details
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

EventCard.propTypes = {
  item: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['default', 'my-events']),
};

/* ── Main page component ─────────────────────────────── */

export default function NewsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [attendanceBusy, setAttendanceBusy] = useState(false);
  const [attendanceError, setAttendanceError] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [isMobileView, setIsMobileView] = useState(false);
  const [activeMobilePanel, setActiveMobilePanel] = useState('myEvents');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 640px)');

    const syncViewport = () => {
      setIsMobileView(mediaQuery.matches);
    };

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);
    return () => mediaQuery.removeEventListener('change', syncViewport);
  }, []);

  useEffect(() => {
    if (isMobileView && activeMobilePanel !== 'myEvents' && activeMobilePanel !== 'calendar') {
      setActiveMobilePanel('myEvents');
    }
  }, [isMobileView, activeMobilePanel]);

  const resolvedUserId = toPositiveInteger(
    user?.id ?? user?.userId ?? user?.user_id ?? null,
  );

  const newsCacheKey = `${NEWS_CACHE_KEY_PREFIX}:${resolvedUserId || 'anon'}`;

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete';

  useEffect(() => {
    let hydratedFromCache = false;

    try {
      const raw = sessionStorage.getItem(newsCacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        const cachedAt = Number(parsed?.cachedAt ?? 0);
        const cachedItems = Array.isArray(parsed?.items) ? parsed.items : null;
        if (cachedItems && cachedAt > 0 && (Date.now() - cachedAt) <= NEWS_CACHE_TTL_MS) {
          const normalizedCached = cachedItems.map(normalizeNewsItem).filter(Boolean);
          setNewsItems(normalizedCached);
          setLoading(false);
          hydratedFromCache = true;
        }
      }
    } catch {
      // Ignore malformed cache payloads.
    }

    const fetchNews = async () => {
      try {
        const params = resolvedUserId ? { userId: resolvedUserId } : undefined;
        const { data } = await client.get('/api/news', { params });
        const normalized = Array.isArray(data)
          ? data.map(normalizeNewsItem).filter(Boolean)
          : [];
        const now = Date.now();
        normalized.sort((a, b) => {
          const aTime = a.startAt ? new Date(a.startAt).getTime() : Infinity;
          const bTime = b.startAt ? new Date(b.startAt).getTime() : Infinity;
          const aUpcoming = aTime >= now;
          const bUpcoming = bTime >= now;
          if (aUpcoming && !bUpcoming) return -1;
          if (!aUpcoming && bUpcoming) return 1;
          if (aUpcoming && bUpcoming) return aTime - bTime;
          return bTime - aTime;
        });
        setNewsItems(normalized);

        try {
          sessionStorage.setItem(newsCacheKey, JSON.stringify({
            cachedAt: Date.now(),
            items: normalized,
          }));
        } catch {
          // Ignore cache write failures.
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        if (!hydratedFromCache) {
          setLoading(false);
        }
      }
    };
    fetchNews();
  }, [newsCacheKey, resolvedUserId]);

  const applyAttendanceUpdate = (eventId, payload) => {
    const nextAttendeeCount = Number(payload?.attendeeCount ?? 0);
    const nextStatus = payload?.userAttendanceStatus ?? null;
    const nextIsAttending = Boolean(payload?.isUserAttending);
    const nextIsFull = Boolean(payload?.isFull);
    const nextRemaining = payload?.remainingSeats ?? null;

    setNewsItems((previous) => previous.map((item) => {
      if (Number(item.id) !== Number(eventId)) {
        return item;
      }
      return {
        ...item,
        attendeeCount: nextAttendeeCount,
        userAttendanceStatus: nextStatus,
        isUserAttending: nextIsAttending,
        isFull: nextIsFull,
        remainingSeats: nextRemaining,
      };
    }));

    setSelectedArticle((previous) => {
      if (!previous || Number(previous.id) !== Number(eventId)) {
        return previous;
      }
      return {
        ...previous,
        attendeeCount: nextAttendeeCount,
        userAttendanceStatus: nextStatus,
        isUserAttending: nextIsAttending,
        isFull: nextIsFull,
        remainingSeats: nextRemaining,
      };
    });
  };

  const handleToggleAttendance = async () => {
    if (!selectedArticle?.id || !resolvedUserId) {
      setAttendanceError('Please sign in to manage attendance.');
      return;
    }

    setAttendanceBusy(true);
    setAttendanceError('');

    const eventId = selectedArticle.id;
    const isCancelling = Boolean(selectedArticle.isUserAttending);
    const endpoint = isCancelling
      ? `/api/news/${eventId}/attendance/cancel`
      : `/api/news/${eventId}/attendance`;

    try {
      const { data } = await client.post(endpoint, { userId: resolvedUserId });
      applyAttendanceUpdate(eventId, data ?? {});
    } catch (error) {
      const code = error?.response?.data?.code;
      const message = error?.response?.data?.message;
      if (code === 'EVENT_FULL') {
        setAttendanceError('Event is already full.');
      } else {
        setAttendanceError(message || 'Unable to update attendance right now.');
      }
    } finally {
      setAttendanceBusy(false);
    }
  };

  const myEvents = newsItems.filter((item) => {
    return isAttendingItem(item);
  });

  const currentMonthCalendar = useMemo(() => {
    const today = new Date();
    const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const monthEnd = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);

    const eventsByDay = newsItems.reduce((accumulator, item) => {
      const value = getPrimaryEventDate(item);
      if (!value) {
        return accumulator;
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return accumulator;
      }
      if (
        date.getFullYear() !== monthStart.getFullYear()
        || date.getMonth() !== monthStart.getMonth()
      ) {
        return accumulator;
      }

      const dayNumber = date.getDate();
      const currentItems = accumulator.get(dayNumber) ?? [];
      currentItems.push(item);
      accumulator.set(dayNumber, currentItems);
      return accumulator;
    }, new Map());

    eventsByDay.forEach((items, dayNumber) => {
      const sortedItems = [...items].sort((left, right) => {
        const leftDate = getPrimaryEventDate(left);
        const rightDate = getPrimaryEventDate(right);
        const leftTime = leftDate ? new Date(leftDate).getTime() : Number.MAX_SAFE_INTEGER;
        const rightTime = rightDate ? new Date(rightDate).getTime() : Number.MAX_SAFE_INTEGER;
        return leftTime - rightTime;
      });
      eventsByDay.set(dayNumber, sortedItems);
    });

    const allEventDayNumbers = new Set(eventsByDay.keys());

    const attendedEventDayNumbers = new Set(
      [...eventsByDay.entries()]
        .filter(([, items]) => items.some((item) => isAttendingItem(item)))
        .map(([dayNumber]) => dayNumber),
    );

    const leadingEmpty = monthStart.getDay();
    const trailingEmpty = 6 - monthEnd.getDay();
    const dayCells = [];

    for (let i = 0; i < leadingEmpty; i += 1) {
      dayCells.push({
        key: `pad-start-${i}`,
        day: null,
        isEvent: false,
        isAttendedEvent: false,
        isToday: false,
        events: [],
      });
    }

    for (let day = 1; day <= monthEnd.getDate(); day += 1) {
      const isAttendedEvent = attendedEventDayNumbers.has(day);
      const events = eventsByDay.get(day) ?? [];
      const preferredEvent = getPreferredCalendarEvent(events);
      const tone = preferredEvent ? getCategoryMeta(preferredEvent.category).tone : null;
      dayCells.push({
        key: `day-${day}`,
        day,
        isAttendedEvent,
        isEvent: allEventDayNumbers.has(day),
        isToday:
          day === today.getDate()
          && monthStart.getMonth() === today.getMonth()
          && monthStart.getFullYear() === today.getFullYear(),
        events,
        tone,
      });
    }

    for (let i = 0; i < trailingEmpty; i += 1) {
      dayCells.push({
        key: `pad-end-${i}`,
        day: null,
        isEvent: false,
        isAttendedEvent: false,
        isToday: false,
        events: [],
      });
    }

    return {
      monthLabel: monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      dayCells,
    };
  }, [calendarMonth, newsItems]);

  const handlePreviousMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleCalendarDaySelect = (eventsForDay) => {
    if (!Array.isArray(eventsForDay) || eventsForDay.length === 0) {
      return;
    }
    const preferredEvent = getPreferredCalendarEvent(eventsForDay);
    setSelectedArticle(preferredEvent);
  };

  const getCalendarPreviewLabel = (eventsForDay) => {
    if (!Array.isArray(eventsForDay) || eventsForDay.length === 0) {
      return '';
    }
    const preferredEvent = getPreferredCalendarEvent(eventsForDay);
    const primaryTitle = String(preferredEvent?.title ?? 'Scheduled event').trim();
    if (eventsForDay.length === 1) {
      return primaryTitle;
    }
    return `${primaryTitle} +${eventsForDay.length - 1} more`;
  };

  useEffect(() => {
    if (selectedArticle) {
      document.body.style.overflow = 'hidden';
      setAttendanceError('');
    } else {
      document.body.style.overflow = '';
      setAttendanceError('');
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedArticle]);

  if (!isVerified) {
    return (
      <AppLayout title="Events" user={user} onLogout={onLogout}>
        <div className="news-page">
          <div className="rejection-lockout" style={{
            maxWidth: '600px',
            margin: '64px auto',
            background: 'var(--color-surface, #ffffff)',
            padding: '48px',
            borderRadius: '24px',
            textAlign: 'center',
            boxShadow: 'var(--shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06))',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              width: '72px',
              height: '72px',
              background: '#FFFBEB',
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              margin: '0 auto 24px',
              color: '#F59E0B'
            }}>
              <AlertCircle size={36} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px', color: '#111827' }}>Verification Required</h1>
            <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '32px', lineHeight: '1.6' }}>
              {isIncomplete
                ? "Your membership profile is incomplete. Please verify your details to access union events and updates."
                : "Your membership application is still under review. Once approved, you will gain access to union events."}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              {isIncomplete ? (
                <button
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '999px',
                    border: 'none',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  View Application
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '12px 24px',
                  borderRadius: '999px',
                  border: '1px solid #d1d5db',
                  fontWeight: '600',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Events" user={user} onLogout={onLogout}>
      <div className="ev-page">
        {/* Page header */}
        <section className="ev-page__intro">
          <div className="ev-page__intro-icon">
            <Megaphone size={22} />
          </div>
          <div>
            <h2>Events</h2>
            <p>Browse upcoming union events, assemblies, and activities.</p>
          </div>
          {!loading && newsItems.length > 0 && (
            <span className="ev-page__count">{newsItems.length} event{newsItems.length !== 1 ? 's' : ''}</span>
          )}
        </section>

        {/* Content */}
        {loading ? (
          <div className="ev-page__empty">
            <div className="ev-page__spinner" />
            <p>Loading events...</p>
          </div>
        ) : newsItems.length === 0 ? (
          <div className="ev-page__empty">
            <CalendarDays size={48} />
            <h3>No events available</h3>
            <p>Check back soon for upcoming union events and activities.</p>
          </div>
        ) : (
          <div className="ev-page__body">
            <div className="ev-page__top-grid">
              {isMobileView && (
                <div className="ev-page__mobile-panel-tabs" role="tablist" aria-label="Events mobile panels">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMobilePanel === 'myEvents'}
                    className={`ev-page__mobile-panel-tab${activeMobilePanel === 'myEvents' ? ' is-active' : ''}`}
                    onClick={() => setActiveMobilePanel('myEvents')}
                  >
                    My Events
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeMobilePanel === 'calendar'}
                    className={`ev-page__mobile-panel-tab${activeMobilePanel === 'calendar' ? ' is-active' : ''}`}
                    onClick={() => setActiveMobilePanel('calendar')}
                  >
                    Calendar
                  </button>
                </div>
              )}

              {(!isMobileView || activeMobilePanel === 'myEvents') && (
              <section id="ev-my-events-panel" className="ev-page__group ev-page__group--my-events" aria-label="My Events">
                <div className="ev-page__section-label">My Events</div>
                {myEvents.length === 0 ? (
                  <div className="ev-page__group-empty ev-page__group-empty--fill">
                    <p>You have not joined any events yet.</p>
                  </div>
                ) : (
                  <div className="ev-my-events-shell">
                    <div className="ev-list ev-list--compact ev-list--my-events">
                      {myEvents.map((item) => (
                        <EventCard
                          key={`my-event-${item.id}`}
                          item={item}
                          onSelect={setSelectedArticle}
                          variant="my-events"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </section>
              )}

              {(!isMobileView || activeMobilePanel === 'calendar') && (
              <aside id="ev-calendar-panel" className="ev-calendar" aria-label="Events calendar">
                <div className="ev-calendar__title-row">
                  <h3>Calendar</h3>
                </div>
                <div className="ev-calendar__header">
                  <button type="button" onClick={handlePreviousMonth} aria-label="Previous month">
                    &#8592;
                  </button>
                  <p>{currentMonthCalendar.monthLabel}</p>
                  <button type="button" onClick={handleNextMonth} aria-label="Next month">
                    &#8594;
                  </button>
                </div>
                <div className="ev-calendar__body">
                  <div className="ev-calendar__weekdays">
                    {CALENDAR_WEEK_LABELS.map((label) => (
                      <span key={label}>{label}</span>
                    ))}
                  </div>
                  <div className="ev-calendar__grid">
                    {currentMonthCalendar.dayCells.map((cell) => {
                      const dayClassName = `ev-calendar__day${
                        cell.day === null
                          ? ' ev-calendar__day--empty'
                          : cell.isAttendedEvent
                            ? ' ev-calendar__day--attended'
                            : cell.isEvent
                              ? ' ev-calendar__day--event'
                              : ''
                      }${cell.isToday ? ' ev-calendar__day--today' : ''}${
                        cell.events.length > 0 ? ' ev-calendar__day--interactive' : ''
                      }${cell.tone ? ` ev-calendar__day--tone-${cell.tone}` : ''
                      }`;

                      if (cell.day === null) {
                        return <div key={cell.key} className={dayClassName} />;
                      }

                      if (cell.events.length > 0) {
                        return (
                          <button
                            key={cell.key}
                            type="button"
                            className={dayClassName}
                            onClick={() => handleCalendarDaySelect(cell.events)}
                            data-event-preview={getCalendarPreviewLabel(cell.events)}
                            aria-label={`Open ${cell.events.length} event${cell.events.length > 1 ? 's' : ''} on ${currentMonthCalendar.monthLabel} ${cell.day}`}
                          >
                            <span className="ev-calendar__day-number">{cell.day}</span>
                          </button>
                        );
                      }

                      return (
                        <div key={cell.key} className={dayClassName}>
                          <span className="ev-calendar__day-number">{cell.day}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </aside>
              )}
            </div>

            <div className="ev-page__section-label ev-page__section-label--all-events">All Events</div>
            <div className="ev-list ev-list--all-events">
              {newsItems.map((item) => (
                <EventCard key={item.id} item={item} onSelect={setSelectedArticle} />
              ))}
            </div>
          </div>
        )}

        {/* ── Detail Modal ──────────────────────────────── */}
        {selectedArticle && (
          <div className="ev-overlay" onClick={() => setSelectedArticle(null)}>
            <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="ev-modal__close" onClick={() => setSelectedArticle(null)} aria-label="Close">
                <X size={18} />
              </button>

              {/* Left: image panel */}
              <div className="ev-modal__visual">
                {selectedArticle.heroImage ? (
                  <img src={selectedArticle.heroImage} alt={selectedArticle.title} className="ev-modal__img" />
                ) : (
                  <div className="ev-modal__img-ph"><CalendarDays size={48} /></div>
                )}
              </div>

              {/* Right: details panel */}
              <div className="ev-modal__details">
                <div className="ev-modal__scroll">
                  {/* badges */}
                  <div className="ev-modal__badges">
                    <span className={`ev-cat ev-cat--${getCategoryMeta(selectedArticle.category).tone}`}>
                      <Tag size={11} />{getCategoryMeta(selectedArticle.category).label}
                    </span>
                    <span className="ev-cat ev-cat--capacity"><Users size={11} />{formatSeatSummary(selectedArticle)}</span>
                    {selectedArticle.isUserAttending ? (
                      <span className="ev-cat ev-cat--green">Attending</span>
                    ) : null}
                    {!selectedArticle.isUserAttending && selectedArticle.isFull ? (
                      <span className="ev-cat ev-cat--orange">Full</span>
                    ) : null}
                  </div>

                  {/* title */}
                  <h2 className="ev-modal__title">{selectedArticle.title}</h2>

                  {/* info rows */}
                  <div className="ev-modal__info">
                    <div className="ev-modal__info-row">
                      <Calendar size={15} />
                      <span>
                        {(() => {
                          const primaryDate = selectedArticle.startAt ?? selectedArticle.publishedAt;
                          if (!primaryDate) return 'To be announced';
                          return formatDate(primaryDate);
                        })()}
                        {selectedArticle.endAt && selectedArticle.endAt !== selectedArticle.startAt && ` — ${formatDate(selectedArticle.endAt)}`}
                      </span>
                    </div>
                    {selectedArticle.startAt && (
                      <div className="ev-modal__info-row">
                        <Clock size={15} />
                        <span>{formatTime(selectedArticle.startAt)}</span>
                      </div>
                    )}
                    {selectedArticle.venue && (
                      <div className="ev-modal__info-row">
                        <MapPin size={15} />
                        <span>{selectedArticle.venue}</span>
                      </div>
                    )}
                  </div>

                  {/* description */}
                  <div className="ev-modal__body">
                    {selectedArticle.content ? (
                      selectedArticle.content.split('\n').map((paragraph, idx) => (
                        paragraph.trim() ? <p key={idx}>{paragraph}</p> : null
                      ))
                    ) : selectedArticle.summary ? (
                      <p>{selectedArticle.summary}</p>
                    ) : (
                      <p className="ev-modal__placeholder">No additional details provided for this event.</p>
                    )}
                  </div>
                </div>

                {/* external link — pinned to bottom */}
                <div className="ev-modal__footer">
                  <div className="ev-modal__footer-actions">
                  <button
                    type="button"
                    onClick={handleToggleAttendance}
                    disabled={attendanceBusy || !resolvedUserId || (!selectedArticle.isUserAttending && selectedArticle.isFull)}
                    className="ev-modal__action ev-modal__action--primary"
                  >
                    {attendanceBusy
                      ? 'Please wait...'
                      : !resolvedUserId
                      ? 'Sign in to Attend'
                      : selectedArticle.isUserAttending
                      ? 'Cancel Attendance'
                      : selectedArticle.isFull
                      ? 'Event is Full'
                      : 'Attend Event'}
                  </button>

                  {selectedArticle.externalLink && (
                    <a
                      href={selectedArticle.externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ev-modal__action ev-modal__action--secondary"
                    >
                      Visit External Link <ExternalLink size={15} />
                    </a>
                  )}
                  </div>

                  {!selectedArticle.externalLink && (
                    <span className="ev-modal__assist">
                      No external link provided for this event.
                    </span>
                  )}

                  {attendanceError ? (
                    <span className="ev-modal__error">{attendanceError}</span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

NewsPage.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    isApproved: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.number]),
  }),
  onLogout: PropTypes.func.isRequired,
};
